/**
 * Utilidades de validación y sanitización (COMPARTIDO)
 *
 * Este módulo es compartido entre client y server.
 * Usa CommonJS exports que son compatibles con ambos:
 * - Node.js server (require directo)
 * - Client bundlers (webpack/vite transpilan CommonJS a ES6)
 *
 * ⚠️ FUNCIONES CRÍTICAS DE SEGURIDAD ⚠️
 * Estas funciones protegen contra ataques XSS y deben mantenerse idénticas
 * en client y server para consistencia y seguridad.
 */

/**
 * Sanitize text input by removing HTML tags and dangerous content
 *
 * NOTE: This function does NOT escape special characters (<>&"') because React
 * automatically escapes them when rendering, which prevents XSS. If we escape
 * manually, React will double-escape them, showing "&amp;" instead of "&".
 *
 * @param {string} input - The input string to sanitize
 * @returns {string} - Sanitized string
 */
const sanitizeTextInput = (input) => {
  if (typeof input !== 'string') return '';

  return input
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove script tags content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Normalize multiple spaces to single space
    .replace(/\s+/g, ' ')
    // Trim whitespace
    .trim();
};

/**
 * Sanitize text input for real-time onChange events (allows spaces while typing)
 *
 * Unlike sanitizeTextInput(), this function:
 * - Does NOT trim whitespace (allows typing spaces)
 * - Normalizes multiple consecutive spaces to single space
 * - Removes HTML tags and script content
 *
 * Use this for onChange handlers. Use sanitizeTextInput() for final submit.
 *
 * NOTE: This function does NOT escape special characters (<>&"') because React
 * automatically escapes them when rendering, which prevents XSS.
 *
 * @param {string} input - The input string to sanitize
 * @returns {string} - Sanitized string (with spaces preserved)
 */
const sanitizeTextInputRealTime = (input) => {
  if (typeof input !== 'string') return '';

  return input
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove script tags content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Normalize multiple spaces to single space (but don't trim)
    .replace(/\s+/g, ' ');
  // Note: NO .trim() here - allows spaces at start/end during typing
};

/**
 * Validate if a string contains only safe characters for display
 *
 * @param {string} input - Input to validate
 * @returns {boolean} - True if safe, false otherwise
 */
const isInputSafe = (input) => {
  if (typeof input !== 'string') return false;

  // Check for dangerous patterns
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /vbscript:/i,
    /onload=/i,
    /onerror=/i,
    /onclick=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i
  ];

  return !dangerousPatterns.some(pattern => pattern.test(input));
};

/**
 * Sanitize location input (for origin/destination fields)
 * Allows spaces, letters, numbers, and common location characters
 *
 * @param {string} input - The location string to sanitize
 * @returns {string} - Sanitized location string
 */
const sanitizeLocationInput = (input) => {
  if (typeof input !== 'string') return '';

  return input
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove script tags content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Allow spaces, letters, numbers, commas, periods, hyphens, parentheses
    // but escape dangerous HTML characters
    .replace(/[<>&"']/g, (match) => {
      const htmlEntities = {
        '<': '&lt;',
        '>': '&gt;',
        '&': '&amp;',
        '"': '&quot;',
        "'": '&#x27;'
      };
      return htmlEntities[match];
    })
    // Keep spaces but normalize multiple spaces to single space
    .replace(/\s+/g, ' ')
    // Trim whitespace from start and end
    .trim()
    // Limit length to 255 characters (matching database field)
    .substring(0, 255);
};

/**
 * Sanitize name/lastname input for real-time input masking
 * Only allows letters (no spaces, numbers or special chars except Latin chars)
 *
 * @param {string} input - The name/lastname to sanitize
 * @returns {string} - Sanitized and capitalized name
 */
const sanitizeNameInput = (input) => {
  if (typeof input !== 'string') return '';

  // Solo permitir letras (incluyendo caracteres latinos)
  let sanitized = input.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ]/g, '');

  // Limitar a 50 caracteres
  sanitized = sanitized.substring(0, 50);

  // Primera letra en mayúscula, resto en minúsculas
  if (sanitized.length > 0) {
    sanitized = sanitized.charAt(0).toUpperCase() + sanitized.slice(1).toLowerCase();
  }

  return sanitized;
};

/**
 * Validate Chilean phone number format
 *
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid Chilean format
 */
const validateChileanPhone = (phone) => {
  if (!phone) return false;
  const regex = /^(\+?56)?(\s?)?(9)(\s?)?(\d{4})(\s?)?(\d{4})$/;
  return regex.test(phone);
};

/**
 * Sanitize and normalize Chilean phone number
 * Unified function for client and server use
 *
 * @param {string} phone - Phone number to sanitize
 * @param {object} options - Configuration options
 * @param {boolean} options.formatted - If true, returns "+56 9 XXXX XXXX", else "+569XXXXXXXX"
 * @param {boolean} options.returnEmpty - If true, returns '' on invalid, else null
 * @returns {string|null} - Formatted phone or null/''
 */
const sanitizePhone = (phone, options = {}) => {
  const { formatted = false, returnEmpty = false } = options;

  if (!phone) return returnEmpty ? '' : null;

  // Remove spaces, hyphens and parentheses
  let cleaned = phone.replace(/[\s\-()]/g, '');

  // If starts with 9 and has 9 digits, it's Chilean format without country code
  if (cleaned.startsWith('9') && cleaned.length === 9) {
    cleaned = '+569' + cleaned.substring(1);
  }
  // If no + and has 11 digits starting with 56
  else if (!cleaned.startsWith('+') && cleaned.startsWith('56') && cleaned.length === 11) {
    cleaned = '+' + cleaned;
  }
  // If has 8 digits, assume Chilean number without the 9
  else if (cleaned.length === 8 && !cleaned.startsWith('9')) {
    cleaned = '+569' + cleaned;
  }

  // Validate final format
  if (!validateChileanPhone(cleaned)) {
    return returnEmpty ? '' : null;
  }

  // Format output based on options
  const match = cleaned.match(/^(\+56)?(9)(\d{4})(\d{4})$/);
  if (match) {
    return formatted ? `+56 9 ${match[3]} ${match[4]}` : `+569${match[3]}${match[4]}`;
  }

  return cleaned;
};

/**
 * Limpia un RUT removiendo puntos y espacios, dejando solo números y guión
 *
 * @param {string} rut - RUT a limpiar
 * @returns {string} - RUT limpio (ej: "12345678-9")
 */
const cleanRut = (rut) => {
  if (typeof rut !== 'string') return '';
  return rut.replace(/[.\s]/g, '').toUpperCase();
};

/**
 * Valida formato y dígito verificador de RUT chileno
 *
 * Acepta formatos:
 * - 12345678-9
 * - 12.345.678-9
 * - 12345678-K
 *
 * @param {string} rut - RUT a validar
 * @returns {boolean} - True si el RUT es válido
 */
const validateRut = (rut) => {
  if (!rut || typeof rut !== 'string') return false;

  // Limpiar RUT
  const cleaned = cleanRut(rut);

  // Validar formato básico: números-dígito
  const rutRegex = /^(\d{1,8})-?([\dkK])$/;
  const match = cleaned.match(rutRegex);

  if (!match) return false;

  const [, numbers, providedDv] = match;

  // Calcular dígito verificador esperado
  let sum = 0;
  let multiplier = 2;

  // Recorrer de derecha a izquierda
  for (let i = numbers.length - 1; i >= 0; i--) {
    sum += parseInt(numbers[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const expectedDv = 11 - (sum % 11);
  let calculatedDv;

  if (expectedDv === 11) {
    calculatedDv = '0';
  } else if (expectedDv === 10) {
    calculatedDv = 'K';
  } else {
    calculatedDv = expectedDv.toString();
  }

  // Comparar dígito verificador
  return providedDv.toUpperCase() === calculatedDv;
};

/**
 * Formatea un RUT al formato estándar chileno XX.XXX.XXX-X
 *
 * @param {string} rut - RUT a formatear
 * @returns {string} - RUT formateado o string vacío si inválido
 */
const formatRut = (rut) => {
  if (!rut || typeof rut !== 'string') return '';

  // Limpiar y validar
  const cleaned = cleanRut(rut);
  if (!validateRut(cleaned)) return '';

  // Separar números y dígito verificador
  const match = cleaned.match(/^(\d{1,8})-?([\dkK])$/);
  if (!match) return '';

  const [, numbers, dv] = match;

  // Formatear con puntos
  const formatted = numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${formatted}-${dv.toUpperCase()}`;
};

/**
 * Valida formato de email
 * @param {string} email - Email a validar
 * @returns {{valid: boolean, message: string}}
 */
const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { valid: false, message: 'El email es requerido' };
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, message: 'Formato de email inválido' };
  }
  return { valid: true, message: '' };
};

/**
 * Valida formato básico de contraseña
 * @param {string} password - Contraseña a validar
 * @returns {{valid: boolean, message: string}}
 */
const validatePassword = (password) => {
  if (!password || typeof password !== 'string') {
    return { valid: false, message: 'La contraseña es requerida' };
  }
  if (password.length < 6) {
    return { valid: false, message: 'La contraseña debe tener al menos 6 caracteres' };
  }
  return { valid: true, message: '' };
};

/**
 * Valida formato de fecha (YYYY-MM-DD)
 * @param {string} fecha - Fecha a validar
 * @param {string} [fieldName='fecha'] - Nombre del campo para mensajes
 * @returns {{valid: boolean, message: string, value?: Date}}
 */
const validateDate = (fecha, fieldName = 'fecha') => {
  if (!fecha) {
    return { valid: false, message: `${fieldName} es requerida` };
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(fecha)) {
    return {
      valid: false,
      message: `Formato de ${fieldName} inválido (debe ser YYYY-MM-DD)`
    };
  }

  const fechaObj = new Date(fecha);
  if (isNaN(fechaObj.getTime())) {
    return { valid: false, message: `${fieldName} no es válida` };
  }

  return { valid: true, message: '', value: fechaObj };
};

/**
 * Valida rango de fechas (inicio < término)
 * @param {string} fechaInicio - Fecha de inicio
 * @param {string} fechaTermino - Fecha de término (opcional)
 * @param {string} [inicioLabel='Fecha de inicio'] - Label para inicio
 * @param {string} [terminoLabel='Fecha de término'] - Label para término
 * @returns {{valid: boolean, message: string}}
 */
const validateDateRange = (
  fechaInicio,
  fechaTermino,
  inicioLabel = 'Fecha de inicio',
  terminoLabel = 'Fecha de término'
) => {
  const validacionInicio = validateDate(fechaInicio, inicioLabel);
  if (!validacionInicio.valid) {
    return validacionInicio;
  }

  if (fechaTermino) {
    const validacionTermino = validateDate(fechaTermino, terminoLabel);
    if (!validacionTermino.valid) {
      return validacionTermino;
    }

    if (validacionTermino.value < validacionInicio.value) {
      return {
        valid: false,
        message: `${terminoLabel} debe ser posterior a ${inicioLabel}`
      };
    }
  }

  return { valid: true, message: '' };
};

// CommonJS export (compatible con Node.js y bundlers modernos)
module.exports = {
  sanitizeTextInput,
  sanitizeTextInputRealTime,
  isInputSafe,
  sanitizeLocationInput,
  sanitizeNameInput,
  validateChileanPhone,
  sanitizePhone,
  cleanRut,
  validateRut,
  formatRut,
  validateEmail,
  validatePassword,
  validateDate,
  validateDateRange
};
